import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, X, Camera, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// File upload constants
const MAX_FILE_SIZE_MB = 5;
const BYTES_PER_KB = 1024;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * BYTES_PER_KB * BYTES_PER_KB;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const BRAND_OPTIONS = [
  'Louis Vuitton',
  'Prada',
  'Gucci',
  'Hermes',
  'Chanel'
];

const CreateLookPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Pre-fill tags from URL params (e.g. /create?tags=RunwayToRealLife&brand=Louis+Vuitton)
  const prefillTags = searchParams.get('tags') || '';
  const prefillBrand = searchParams.get('brand') || '';

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    tags: prefillTags,
    brand_tags: prefillBrand ? [prefillBrand] : []
  });

  const handleFileUpload = useCallback(async (file) => {
    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP, or GIF image');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`Image must be less than ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    setUploading(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success('Image uploaded successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  }, [token]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, [handleFileUpload]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleBrandToggle = (brand) => {
    setFormData(prev => ({
      ...prev,
      brand_tags: prev.brand_tags.includes(brand)
        ? prev.brand_tags.filter(b => b !== brand)
        : [...prev.brand_tags, brand]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!formData.image_url) {
      toast.error('Please upload an image');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description || null,
        image_url: formData.image_url,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        brand_tags: formData.brand_tags
      };

      await axios.post(`${API}/looks`, payload);
      toast.success('Look shared successfully!');
      navigate('/feed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create look');
    } finally {
      setLoading(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20 bg-[#0a0a0a]" data-testid="create-look-page">
      <div className="py-12 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A] mb-4">Create</p>
            <h1 className="text-4xl font-serif italic text-white">Share Your Look</h1>
            <p className="text-gray-400 mt-2">Show off your style to the fashion community</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Image Upload */}
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Upload Image *
              </Label>
              
              {!formData.image_url ? (
                <div
                  className={`relative border-2 border-dashed rounded-sm transition-all duration-300 ${
                    dragActive 
                      ? 'border-[#C41E3A] bg-[#C41E3A]/10' 
                      : 'border-white/20 hover:border-white/40 bg-[#1c1c1c]'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="file-input"
                  />
                  
                  <div className="flex flex-col items-center justify-center py-16 px-6">
                    {uploading ? (
                      <>
                        <Loader2 className="w-12 h-12 text-[#C41E3A] animate-spin mb-4" />
                        <p className="text-white font-medium">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                          <Upload className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-white font-medium mb-2">
                          Drag & drop your image here
                        </p>
                        <p className="text-gray-500 text-sm mb-4">or</p>
                        <Button
                          type="button"
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/5"
                          onClick={() => fileInputRef.current?.click()}
                          data-testid="browse-btn"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Browse Files
                        </Button>
                        <p className="text-gray-600 text-xs mt-4">
                          JPEG, PNG, WebP or GIF • Max 5MB
                        </p>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <div className="aspect-[4/5] max-w-sm bg-[#1c1c1c] overflow-hidden rounded-sm">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      data-testid="image-preview"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-3 right-3 p-2 bg-black/70 rounded-full hover:bg-black transition-colors"
                    data-testid="remove-image-btn"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                  <p className="text-gray-500 text-sm mt-3">
                    Click the X to remove and upload a different image
                  </p>
                </div>
              )}
            </div>

            {/* Title */}
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Title *
              </Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Summer Elegance with Gucci"
                className="bg-[#1c1c1c] border-white/10 text-white placeholder:text-gray-600"
                maxLength={100}
                data-testid="title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Description
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Tell us about your look, the occasion, and what inspired you..."
                className="bg-[#1c1c1c] border-white/10 text-white placeholder:text-gray-600 min-h-[120px]"
                data-testid="description-input"
              />
            </div>

            {/* Brand Tags */}
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Featured Brands
              </Label>
              <div className="flex flex-wrap gap-3">
                {BRAND_OPTIONS.map((brand) => (
                  <label
                    key={brand}
                    className={`flex items-center gap-2 px-4 py-2 border cursor-pointer transition-all ${
                      formData.brand_tags.includes(brand)
                        ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white'
                        : 'border-white/10 text-gray-400 hover:border-white/30'
                    }`}
                  >
                    <Checkbox
                      checked={formData.brand_tags.includes(brand)}
                      onCheckedChange={() => handleBrandToggle(brand)}
                      className="border-white/20"
                      data-testid={`brand-${brand.toLowerCase().replace(' ', '-')}`}
                    />
                    <span className="text-sm">{brand}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Tags
              </Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="streetwear, summer, minimalist"
                className="bg-[#1c1c1c] border-white/10 text-white placeholder:text-gray-600"
                data-testid="tags-input"
              />
              <p className="text-xs text-gray-500">Separate tags with commas</p>
            </div>

            {/* Submit */}
            <div className="pt-6">
              <Button
                type="submit"
                disabled={loading || uploading}
                className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-4 text-lg font-serif italic"
                data-testid="submit-look-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sharing...
                  </>
                ) : (
                  'Share Look'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateLookPage;
